/**
 * Generated by @openapi-codegen
 *
 * @version 0.1.0
 */
import * as reactQuery from '@tanstack/react-query';
import { useWfbApiContext, WfbApiContext } from './wfbApiContext';
import type * as Fetcher from './wfbApiFetcher';
import { wfbApiFetch } from './wfbApiFetcher';
import type * as Schemas from './wfbApiSchemas';

export type GetAllDriversPathParams = {
  scheduler: string;
  schedulerVersion: string;
};

export type GetAllDriversError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetAllDriversResponse = Schemas.Driver[];

export type GetAllDriversVariables = {
  pathParams: GetAllDriversPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * List all versions of all drivers.
 */
export const fetchGetAllDrivers = (
  variables: GetAllDriversVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    GetAllDriversResponse,
    GetAllDriversError,
    undefined,
    {},
    {},
    GetAllDriversPathParams
  >({
    url: '/v3/driver/{scheduler}/{schedulerVersion}',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * List all versions of all drivers.
 */
export const useGetAllDrivers = <TData = GetAllDriversResponse>(
  variables: GetAllDriversVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetAllDriversResponse,
      GetAllDriversError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<GetAllDriversResponse, GetAllDriversError, TData>({
    queryKey: queryKeyFn({
      path: '/v3/driver/{scheduler}/{schedulerVersion}',
      operationId: 'getAllDrivers',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetAllDrivers({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type GetDriverVersionsPathParams = {
  scheduler: string;
  schedulerVersion: string;
  driverName: string;
};

export type GetDriverVersionsError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetDriverVersionsResponse = Schemas.Driver[];

export type GetDriverVersionsVariables = {
  pathParams: GetDriverVersionsPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * List all versions of a specific driver.
 */
export const fetchGetDriverVersions = (
  variables: GetDriverVersionsVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    GetDriverVersionsResponse,
    GetDriverVersionsError,
    undefined,
    {},
    {},
    GetDriverVersionsPathParams
  >({
    url: '/v3/driver/{scheduler}/{schedulerVersion}/{driverName}',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * List all versions of a specific driver.
 */
export const useGetDriverVersions = <TData = GetDriverVersionsResponse>(
  variables: GetDriverVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetDriverVersionsResponse,
      GetDriverVersionsError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    GetDriverVersionsResponse,
    GetDriverVersionsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/driver/{scheduler}/{schedulerVersion}/{driverName}',
      operationId: 'getDriverVersions',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetDriverVersions({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type GetDriverPathParams = {
  scheduler: string;
  schedulerVersion: string;
  driverName: string;
  driverVersion: string;
};

export type GetDriverError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetDriverVariables = {
  pathParams: GetDriverPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Get a specific version of a driver.
 */
export const fetchGetDriver = (
  variables: GetDriverVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.Driver,
    GetDriverError,
    undefined,
    {},
    {},
    GetDriverPathParams
  >({
    url: '/v3/driver/{scheduler}/{schedulerVersion}/{driverName}/{driverVersion}',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Get a specific version of a driver.
 */
export const useGetDriver = <TData = Schemas.Driver>(
  variables: GetDriverVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Driver, GetDriverError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<Schemas.Driver, GetDriverError, TData>({
    queryKey: queryKeyFn({
      path: '/v3/driver/{scheduler}/{schedulerVersion}/{driverName}/{driverVersion}',
      operationId: 'getDriver',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetDriver({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type CreateTransportConfigError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type CreateTransportConfigVariables = {
  body: Schemas.CreateTransportConfigInput;
} & WfbApiContext['fetcherOptions'];

/**
 * Create a new transport configuration with validation.
 */
export const fetchCreateTransportConfig = (
  variables: CreateTransportConfigVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.TransportConfigMetadata,
    CreateTransportConfigError,
    Schemas.CreateTransportConfigInput,
    {},
    {},
    {}
  >({ url: '/v3/workcell/transport', method: 'post', ...variables, signal });

/**
 * Create a new transport configuration with validation.
 */
export const useCreateTransportConfig = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.TransportConfigMetadata,
      CreateTransportConfigError,
      CreateTransportConfigVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.TransportConfigMetadata,
    CreateTransportConfigError,
    CreateTransportConfigVariables
  >({
    mutationFn: (variables: CreateTransportConfigVariables) =>
      fetchCreateTransportConfig({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type UpdateTransportConfigError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type UpdateTransportConfigVariables = {
  body: Schemas.UpdateTransportConfigInput;
} & WfbApiContext['fetcherOptions'];

/**
 * Update an existing transport configuration.
 */
export const fetchUpdateTransportConfig = (
  variables: UpdateTransportConfigVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.TransportConfigMetadata,
    UpdateTransportConfigError,
    Schemas.UpdateTransportConfigInput,
    {},
    {},
    {}
  >({ url: '/v3/workcell/transport', method: 'put', ...variables, signal });

/**
 * Update an existing transport configuration.
 */
export const useUpdateTransportConfig = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.TransportConfigMetadata,
      UpdateTransportConfigError,
      UpdateTransportConfigVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.TransportConfigMetadata,
    UpdateTransportConfigError,
    UpdateTransportConfigVariables
  >({
    mutationFn: (variables: UpdateTransportConfigVariables) =>
      fetchUpdateTransportConfig({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type GetTransportConfigQueryParams = {
  /**
   * @format uuid
   */
  id: string;
  type: 'V1' | 'V3';
  /**
   * Version number of the configuration (optional, defaults to latest)
   */
  version?: number | null;
};

export type GetTransportConfigError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetTransportConfigVariables = {
  queryParams: GetTransportConfigQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Get transport configuration with metadata. If version is not specified, returns latest version.
 */
export const fetchGetTransportConfig = (
  variables: GetTransportConfigVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.TransportConfigWithData,
    GetTransportConfigError,
    undefined,
    {},
    GetTransportConfigQueryParams,
    {}
  >({ url: '/v3/workcell/transport', method: 'get', ...variables, signal });

/**
 * Get transport configuration with metadata. If version is not specified, returns latest version.
 */
export const useGetTransportConfig = <TData = Schemas.TransportConfigWithData>(
  variables: GetTransportConfigVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.TransportConfigWithData,
      GetTransportConfigError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    Schemas.TransportConfigWithData,
    GetTransportConfigError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/workcell/transport',
      operationId: 'getTransportConfig',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetTransportConfig({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type RenameTransportConfigError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type RenameTransportConfigVariables = {
  body: Schemas.RenameTransportConfigInput;
} & WfbApiContext['fetcherOptions'];

/**
 * Rename an existing transport configuration.
 */
export const fetchRenameTransportConfig = (
  variables: RenameTransportConfigVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.TransportConfigMetadata,
    RenameTransportConfigError,
    Schemas.RenameTransportConfigInput,
    {},
    {},
    {}
  >({ url: '/v3/workcell/transport', method: 'patch', ...variables, signal });

/**
 * Rename an existing transport configuration.
 */
export const useRenameTransportConfig = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.TransportConfigMetadata,
      RenameTransportConfigError,
      RenameTransportConfigVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.TransportConfigMetadata,
    RenameTransportConfigError,
    RenameTransportConfigVariables
  >({
    mutationFn: (variables: RenameTransportConfigVariables) =>
      fetchRenameTransportConfig({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DeleteTransportConfigQueryParams = {
  /**
   * @format uuid
   */
  id: string;
  type: 'V1' | 'V3';
};

export type DeleteTransportConfigError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type DeleteTransportConfigVariables = {
  queryParams: DeleteTransportConfigQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Delete a transport configuration.
 */
export const fetchDeleteTransportConfig = (
  variables: DeleteTransportConfigVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    undefined,
    DeleteTransportConfigError,
    undefined,
    {},
    DeleteTransportConfigQueryParams,
    {}
  >({ url: '/v3/workcell/transport', method: 'delete', ...variables, signal });

/**
 * Delete a transport configuration.
 */
export const useDeleteTransportConfig = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteTransportConfigError,
      DeleteTransportConfigVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    undefined,
    DeleteTransportConfigError,
    DeleteTransportConfigVariables
  >({
    mutationFn: (variables: DeleteTransportConfigVariables) =>
      fetchDeleteTransportConfig({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type GetTransportConfigVersionsQueryParams = {
  /**
   * @format uuid
   */
  id: string;
  type: 'V1' | 'V3';
};

export type GetTransportConfigVersionsError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetTransportConfigVersionsResponse = any[][];

export type GetTransportConfigVersionsVariables = {
  queryParams: GetTransportConfigVersionsQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * List all available version numbers for transport configuration.
 */
export const fetchGetTransportConfigVersions = (
  variables: GetTransportConfigVersionsVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    GetTransportConfigVersionsResponse,
    GetTransportConfigVersionsError,
    undefined,
    {},
    GetTransportConfigVersionsQueryParams,
    {}
  >({
    url: '/v3/workcell/transport/versions',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * List all available version numbers for transport configuration.
 */
export const useGetTransportConfigVersions = <
  TData = GetTransportConfigVersionsResponse
>(
  variables: GetTransportConfigVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTransportConfigVersionsResponse,
      GetTransportConfigVersionsError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    GetTransportConfigVersionsResponse,
    GetTransportConfigVersionsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/workcell/transport/versions',
      operationId: 'getTransportConfigVersions',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetTransportConfigVersions(
        { ...fetcherOptions, ...variables },
        signal
      ),
    ...options,
    ...queryOptions,
  });
};

export type GetAllTransportConfigsQueryParams = {
  type?: ('V1' | 'V3') | null;
};

export type GetAllTransportConfigsError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetAllTransportConfigsResponse = Schemas.TransportConfigMetadata[];

export type GetAllTransportConfigsVariables = {
  queryParams?: GetAllTransportConfigsQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * List all transport configurations with metadata.
 */
export const fetchGetAllTransportConfigs = (
  variables: GetAllTransportConfigsVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    GetAllTransportConfigsResponse,
    GetAllTransportConfigsError,
    undefined,
    {},
    GetAllTransportConfigsQueryParams,
    {}
  >({
    url: '/v3/workcell/transport/list',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * List all transport configurations with metadata.
 */
export const useGetAllTransportConfigs = <
  TData = GetAllTransportConfigsResponse
>(
  variables: GetAllTransportConfigsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetAllTransportConfigsResponse,
      GetAllTransportConfigsError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    GetAllTransportConfigsResponse,
    GetAllTransportConfigsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/workcell/transport/list',
      operationId: 'getAllTransportConfigs',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetAllTransportConfigs({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type RestartHubRuntimePathParams = {
  hubSerial: string;
};

export type RestartHubRuntimeError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type RestartHubRuntimeVariables = {
  pathParams: RestartHubRuntimePathParams;
} & WfbApiContext['fetcherOptions'];

export const fetchRestartHubRuntime = (
  variables: RestartHubRuntimeVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.RestartHubOutput,
    RestartHubRuntimeError,
    undefined,
    {},
    {},
    RestartHubRuntimePathParams
  >({
    url: '/v3/workcell/hub/{hubSerial}/restart',
    method: 'post',
    ...variables,
    signal,
  });

export const useRestartHubRuntime = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.RestartHubOutput,
      RestartHubRuntimeError,
      RestartHubRuntimeVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.RestartHubOutput,
    RestartHubRuntimeError,
    RestartHubRuntimeVariables
  >({
    mutationFn: (variables: RestartHubRuntimeVariables) =>
      fetchRestartHubRuntime({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type GetCommandStatusPathParams = {
  commandId: string;
};

export type GetCommandStatusError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetCommandStatusVariables = {
  pathParams: GetCommandStatusPathParams;
} & WfbApiContext['fetcherOptions'];

export const fetchGetCommandStatus = (
  variables: GetCommandStatusVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.CommandStatusOutput,
    GetCommandStatusError,
    undefined,
    {},
    {},
    GetCommandStatusPathParams
  >({
    url: '/v3/workcell/hub/{commandId}/status',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetCommandStatus = <TData = Schemas.CommandStatusOutput>(
  variables: GetCommandStatusVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.CommandStatusOutput,
      GetCommandStatusError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    Schemas.CommandStatusOutput,
    GetCommandStatusError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/workcell/hub/{commandId}/status',
      operationId: 'getCommandStatus',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetCommandStatus({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type GetSchedulerVersionsError = Fetcher.ErrorWrapper<undefined>;

export type GetSchedulerVersionsResponse = {
  [key: string]: string[];
};

export type GetSchedulerVersionsVariables = WfbApiContext['fetcherOptions'];

export const fetchGetSchedulerVersions = (
  variables: GetSchedulerVersionsVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    GetSchedulerVersionsResponse,
    GetSchedulerVersionsError,
    undefined,
    {},
    {},
    {}
  >({
    url: '/v3/workflow/scheduler_versions',
    method: 'get',
    ...variables,
    signal,
  });

export const useGetSchedulerVersions = <TData = GetSchedulerVersionsResponse>(
  variables: GetSchedulerVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetSchedulerVersionsResponse,
      GetSchedulerVersionsError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    GetSchedulerVersionsResponse,
    GetSchedulerVersionsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/workflow/scheduler_versions',
      operationId: 'getSchedulerVersions',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetSchedulerVersions({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type ValidateWorkflowQueryParams = {
  /**
   * Perform validation checking that the workflow is valid for executing on hardware.
   *
   * @default false
   */
  validate_for_execution?: boolean;
  /**
   * Perform validation checking that the workflow is feasible for planning. This is a more expensive check, and can time out for large workflows.
   *
   * @default false
   */
  validate_infeasibility?: boolean;
};

export type ValidateWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type ValidateWorkflowVariables = {
  body: Schemas.WorkflowConfigInputWithParameters;
  queryParams?: ValidateWorkflowQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Validate a workflow.
 */
export const fetchValidateWorkflow = (
  variables: ValidateWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowValidationResult,
    ValidateWorkflowError,
    Schemas.WorkflowConfigInputWithParameters,
    {},
    ValidateWorkflowQueryParams,
    {}
  >({ url: '/v3/workflow/validate', method: 'post', ...variables, signal });

/**
 * Validate a workflow.
 */
export const useValidateWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowValidationResult,
      ValidateWorkflowError,
      ValidateWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowValidationResult,
    ValidateWorkflowError,
    ValidateWorkflowVariables
  >({
    mutationFn: (variables: ValidateWorkflowVariables) =>
      fetchValidateWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type ListWorkflowsPaginatedQueryParams = {
  /**
   * Filter by workflow type (required for efficient pagination)
   */
  workflow_type: Schemas.WorkflowType;
  /**
   * Number of workflows per page
   *
   * @default 100
   */
  page_size?: number | null;
  /**
   * Sort direction - False for newest first (descending), True for oldest first (ascending)
   *
   * @default false
   */
  scan_forward?: boolean;
};

export type ListWorkflowsPaginatedError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type ListWorkflowsPaginatedVariables = {
  body?: Schemas.ListWorkflowsPaginationRequest | null;
  queryParams: ListWorkflowsPaginatedQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * List existing workflows with efficient DynamoDB pagination.
 * The version returned is the most recent version of the workflow.
 *
 * :param workflow_type: Filter by workflow type (required for efficient pagination).
 * :param page_size: Number of workflows per page (query parameter).
 * :param scan_forward: Sort direction - False for newest first (descending), True for oldest first (ascending).
 * :param request: Request body containing pagination cursor.
 * :return: ListWorkflowsResult with workflows and pagination info.
 */
export const fetchListWorkflowsPaginated = (
  variables: ListWorkflowsPaginatedVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.ListWorkflowsResult,
    ListWorkflowsPaginatedError,
    Schemas.ListWorkflowsPaginationRequest | null,
    {},
    ListWorkflowsPaginatedQueryParams,
    {}
  >({ url: '/v3/workflow/paginated', method: 'post', ...variables, signal });

/**
 * List existing workflows with efficient DynamoDB pagination.
 * The version returned is the most recent version of the workflow.
 *
 * :param workflow_type: Filter by workflow type (required for efficient pagination).
 * :param page_size: Number of workflows per page (query parameter).
 * :param scan_forward: Sort direction - False for newest first (descending), True for oldest first (ascending).
 * :param request: Request body containing pagination cursor.
 * :return: ListWorkflowsResult with workflows and pagination info.
 */
export const useListWorkflowsPaginated = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.ListWorkflowsResult,
      ListWorkflowsPaginatedError,
      ListWorkflowsPaginatedVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.ListWorkflowsResult,
    ListWorkflowsPaginatedError,
    ListWorkflowsPaginatedVariables
  >({
    mutationFn: (variables: ListWorkflowsPaginatedVariables) =>
      fetchListWorkflowsPaginated({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type GetWorkflowPathParams = {
  /**
   * @format uuid
   */
  id: string;
};

export type GetWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetWorkflowVariables = {
  pathParams: GetWorkflowPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Get a workflow configuration.
 */
export const fetchGetWorkflow = (
  variables: GetWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowConfig,
    GetWorkflowError,
    undefined,
    {},
    {},
    GetWorkflowPathParams
  >({ url: '/v3/workflow/{id}', method: 'get', ...variables, signal });

/**
 * Get a workflow configuration.
 */
export const useGetWorkflow = <TData = Schemas.WorkflowConfig>(
  variables: GetWorkflowVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.WorkflowConfig, GetWorkflowError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<Schemas.WorkflowConfig, GetWorkflowError, TData>({
    queryKey: queryKeyFn({
      path: '/v3/workflow/{id}',
      operationId: 'getWorkflow',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetWorkflow({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type ListWorkflowsQueryParams = {
  page?: number | null;
  page_size?: number | null;
  workflow_type?: Schemas.WorkflowType | null;
};

export type ListWorkflowsError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type ListWorkflowsResponse = Schemas.WorkflowInfo[];

export type ListWorkflowsVariables = {
  queryParams?: ListWorkflowsQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * List existing workflows.
 * The version returned is the most recent version of the workflow.
 *
 * :param page: The page number to retrieve.
 * :param page_size: The number of items per page.
 * :param workflow_type: Filter by workflow type (optional).
 * :return: List of existing workflows.
 */
export const fetchListWorkflows = (
  variables: ListWorkflowsVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    ListWorkflowsResponse,
    ListWorkflowsError,
    undefined,
    {},
    ListWorkflowsQueryParams,
    {}
  >({ url: '/v3/workflow', method: 'get', ...variables, signal });

/**
 * List existing workflows.
 * The version returned is the most recent version of the workflow.
 *
 * :param page: The page number to retrieve.
 * :param page_size: The number of items per page.
 * :param workflow_type: Filter by workflow type (optional).
 * :return: List of existing workflows.
 */
export const useListWorkflows = <TData = ListWorkflowsResponse>(
  variables: ListWorkflowsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      ListWorkflowsResponse,
      ListWorkflowsError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<ListWorkflowsResponse, ListWorkflowsError, TData>({
    queryKey: queryKeyFn({
      path: '/v3/workflow',
      operationId: 'listWorkflows',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchListWorkflows({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type CreateWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type CreateWorkflowVariables = {
  body: Schemas.WorkflowConfigInput;
} & WfbApiContext['fetcherOptions'];

/**
 * Create a new workflow.
 */
export const fetchCreateWorkflow = (
  variables: CreateWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowInfo,
    CreateWorkflowError,
    Schemas.WorkflowConfigInput,
    {},
    {},
    {}
  >({ url: '/v3/workflow', method: 'post', ...variables, signal });

/**
 * Create a new workflow.
 */
export const useCreateWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowInfo,
      CreateWorkflowError,
      CreateWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowInfo,
    CreateWorkflowError,
    CreateWorkflowVariables
  >({
    mutationFn: (variables: CreateWorkflowVariables) =>
      fetchCreateWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type UpdateWorkflowPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type UpdateWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type UpdateWorkflowVariables = {
  body: Schemas.WorkflowConfigInput;
  pathParams: UpdateWorkflowPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Update an existing workflow.
 */
export const fetchUpdateWorkflow = (
  variables: UpdateWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowInfo,
    UpdateWorkflowError,
    Schemas.WorkflowConfigInput,
    {},
    {},
    UpdateWorkflowPathParams
  >({ url: '/v3/workflow/{workflowId}', method: 'put', ...variables, signal });

/**
 * Update an existing workflow.
 */
export const useUpdateWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowInfo,
      UpdateWorkflowError,
      UpdateWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowInfo,
    UpdateWorkflowError,
    UpdateWorkflowVariables
  >({
    mutationFn: (variables: UpdateWorkflowVariables) =>
      fetchUpdateWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type RenameWorkflowPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type RenameWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type RenameWorkflowVariables = {
  body: Schemas.RenameWorkflowInput;
  pathParams: RenameWorkflowPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Rename an existing workflow.
 */
export const fetchRenameWorkflow = (
  variables: RenameWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    void,
    RenameWorkflowError,
    Schemas.RenameWorkflowInput,
    {},
    {},
    RenameWorkflowPathParams
  >({
    url: '/v3/workflow/{workflowId}',
    method: 'patch',
    ...variables,
    signal,
  });

/**
 * Rename an existing workflow.
 */
export const useRenameWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      void,
      RenameWorkflowError,
      RenameWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    void,
    RenameWorkflowError,
    RenameWorkflowVariables
  >({
    mutationFn: (variables: RenameWorkflowVariables) =>
      fetchRenameWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DeleteWorkflowPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type DeleteWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type DeleteWorkflowVariables = {
  pathParams: DeleteWorkflowPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Delete an existing workflow.
 */
export const fetchDeleteWorkflow = (
  variables: DeleteWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    undefined,
    DeleteWorkflowError,
    undefined,
    {},
    {},
    DeleteWorkflowPathParams
  >({
    url: '/v3/workflow/{workflowId}',
    method: 'delete',
    ...variables,
    signal,
  });

/**
 * Delete an existing workflow.
 */
export const useDeleteWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteWorkflowError,
      DeleteWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    undefined,
    DeleteWorkflowError,
    DeleteWorkflowVariables
  >({
    mutationFn: (variables: DeleteWorkflowVariables) =>
      fetchDeleteWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type PublishWorkflowPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type PublishWorkflowQueryParams = {
  /**
   * Published workflow will run with simulated drivers.
   *
   * @default false
   */
  simulate_drivers?: boolean;
  /**
   * Published workflow will run with simulated time.
   *
   * @default false
   */
  simulate_time?: boolean;
  /**
   * Reason for publishing the workflow.
   *
   * @default [CODE_REASON_NOT_REQUIRED]
   */
  reason?: string;
  /**
   * When true, overwrites any conflicting workflow on publish.
   *
   * @default false
   */
  overwrite_on_conflict?: boolean;
  /**
   * Plan to include in the published workflow.
   */
  plan_id_to_include?: string | null;
  /**
   * Number of batches to run for the published workflow.
   */
  number_of_batches?: number | null;
};

export type PublishWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type PublishWorkflowRequestBody = Schemas.PublishBodyParameters;

export type PublishWorkflowVariables = {
  body?: PublishWorkflowRequestBody;
  pathParams: PublishWorkflowPathParams;
  queryParams?: PublishWorkflowQueryParams;
} & WfbApiContext['fetcherOptions'];

export const fetchPublishWorkflow = (
  variables: PublishWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.PublishedWorkflow,
    PublishWorkflowError,
    PublishWorkflowRequestBody,
    {},
    PublishWorkflowQueryParams,
    PublishWorkflowPathParams
  >({
    url: '/v3/workflow/{workflowId}/publish',
    method: 'post',
    ...variables,
    signal,
  });

export const usePublishWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.PublishedWorkflow,
      PublishWorkflowError,
      PublishWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.PublishedWorkflow,
    PublishWorkflowError,
    PublishWorkflowVariables
  >({
    mutationFn: (variables: PublishWorkflowVariables) =>
      fetchPublishWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type DeployWorkflowPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type DeployWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type DeployWorkflowVariables = {
  body: Schemas.WorkflowDeployInput;
  pathParams: DeployWorkflowPathParams;
} & WfbApiContext['fetcherOptions'];

export const fetchDeployWorkflow = (
  variables: DeployWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    undefined,
    DeployWorkflowError,
    Schemas.WorkflowDeployInput,
    {},
    {},
    DeployWorkflowPathParams
  >({
    url: '/v3/workflow/{workflowId}/deploy',
    method: 'post',
    ...variables,
    signal,
  });

export const useDeployWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeployWorkflowError,
      DeployWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    undefined,
    DeployWorkflowError,
    DeployWorkflowVariables
  >({
    mutationFn: (variables: DeployWorkflowVariables) =>
      fetchDeployWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type GetWorkflowInfoPathParams = {
  /**
   * @format uuid
   */
  id: string;
};

export type GetWorkflowInfoError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetWorkflowInfoVariables = {
  pathParams: GetWorkflowInfoPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Get the workflow info for a specified workflow.
 */
export const fetchGetWorkflowInfo = (
  variables: GetWorkflowInfoVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowInfo,
    GetWorkflowInfoError,
    undefined,
    {},
    {},
    GetWorkflowInfoPathParams
  >({ url: '/v3/workflow/info/{id}', method: 'get', ...variables, signal });

/**
 * Get the workflow info for a specified workflow.
 */
export const useGetWorkflowInfo = <TData = Schemas.WorkflowInfo>(
  variables: GetWorkflowInfoVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.WorkflowInfo,
      GetWorkflowInfoError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<Schemas.WorkflowInfo, GetWorkflowInfoError, TData>(
    {
      queryKey: queryKeyFn({
        path: '/v3/workflow/info/{id}',
        operationId: 'getWorkflowInfo',
        variables,
      }),
      queryFn: ({ signal }) =>
        fetchGetWorkflowInfo({ ...fetcherOptions, ...variables }, signal),
      ...options,
      ...queryOptions,
    }
  );
};

export type DuplicateWorkflowPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type DuplicateWorkflowError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type DuplicateWorkflowVariables = {
  body?: Schemas.DuplicateWorkflowInput;
  pathParams: DuplicateWorkflowPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Duplicate an existing workflow, creating a new workflow with a new ID and name. If name is not provided, use 'Copy of <original name>'.
 */
export const fetchDuplicateWorkflow = (
  variables: DuplicateWorkflowVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowInfo,
    DuplicateWorkflowError,
    Schemas.DuplicateWorkflowInput,
    {},
    {},
    DuplicateWorkflowPathParams
  >({
    url: '/v3/workflow/{workflowId}/duplicate',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Duplicate an existing workflow, creating a new workflow with a new ID and name. If name is not provided, use 'Copy of <original name>'.
 */
export const useDuplicateWorkflow = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowInfo,
      DuplicateWorkflowError,
      DuplicateWorkflowVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowInfo,
    DuplicateWorkflowError,
    DuplicateWorkflowVariables
  >({
    mutationFn: (variables: DuplicateWorkflowVariables) =>
      fetchDuplicateWorkflow({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type PlanWorkflowStartQueryParams = {
  /**
   * The ID of the workflow to plan.
   *
   * @format uuid
   */
  workflow_id: string;
  /**
   * The ID of the transport config to use.
   */
  transport_config_id?: string | null;
  /**
   * Optional plan ID to use as a hint for planning. Pass a plan UUID or 'latest' to use the most recent plan with a result available. The plan must have a result available to be used as a hint
   */
  hint_plan_id?: string | null;
};

export type PlanWorkflowStartError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type PlanWorkflowStartRequestBody = Schemas.PublishBodyParameters;

export type PlanWorkflowStartVariables = {
  body?: PlanWorkflowStartRequestBody;
  queryParams: PlanWorkflowStartQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Plan a workflow asynchronously.
 *
 * Calling this endpoint triggers a task to plan the workflow in the background and returns an ID that can be used to check the planning status and fetch the results.
 *
 * The status of the task can be checked using the `/v3/workflow/plan/{id}/status` endpoint.
 *
 * The result of the task can be checked using the `/v3/workflow/plan/{id}/result` endpoint.
 */
export const fetchPlanWorkflowStart = (
  variables: PlanWorkflowStartVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowPlanStatus,
    PlanWorkflowStartError,
    PlanWorkflowStartRequestBody,
    {},
    PlanWorkflowStartQueryParams,
    {}
  >({ url: '/v3/workflow/plan', method: 'post', ...variables, signal });

/**
 * Plan a workflow asynchronously.
 *
 * Calling this endpoint triggers a task to plan the workflow in the background and returns an ID that can be used to check the planning status and fetch the results.
 *
 * The status of the task can be checked using the `/v3/workflow/plan/{id}/status` endpoint.
 *
 * The result of the task can be checked using the `/v3/workflow/plan/{id}/result` endpoint.
 */
export const usePlanWorkflowStart = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowPlanStatus,
      PlanWorkflowStartError,
      PlanWorkflowStartVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowPlanStatus,
    PlanWorkflowStartError,
    PlanWorkflowStartVariables
  >({
    mutationFn: (variables: PlanWorkflowStartVariables) =>
      fetchPlanWorkflowStart({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type UploadPlanPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type UploadPlanError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type UploadPlanRequestBody = Schemas.PlanResultInput;

export type UploadPlanVariables = {
  body?: UploadPlanRequestBody;
  pathParams: UploadPlanPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Upload a pre-computed plan result for a workflow.
 *
 * This endpoint allows users to upload externally computed plans into the system,
 * enabling the reuse of pre-computed plans and storing them as if they had been
 * generated by the planner.
 *
 * The uploaded plan is stored in S3 and can be retrieved using the standard
 * plan status and result endpoints.
 */
export const fetchUploadPlan = (
  variables: UploadPlanVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowPlanStatus,
    UploadPlanError,
    UploadPlanRequestBody,
    {},
    {},
    UploadPlanPathParams
  >({
    url: '/v3/workflow/{workflowId}/plan/upload',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Upload a pre-computed plan result for a workflow.
 *
 * This endpoint allows users to upload externally computed plans into the system,
 * enabling the reuse of pre-computed plans and storing them as if they had been
 * generated by the planner.
 *
 * The uploaded plan is stored in S3 and can be retrieved using the standard
 * plan status and result endpoints.
 */
export const useUploadPlan = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowPlanStatus,
      UploadPlanError,
      UploadPlanVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowPlanStatus,
    UploadPlanError,
    UploadPlanVariables
  >({
    mutationFn: (variables: UploadPlanVariables) =>
      fetchUploadPlan({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type GetPlanStatusPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
  /**
   * @format uuid
   */
  planId: string;
};

export type GetPlanStatusError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetPlanStatusVariables = {
  pathParams: GetPlanStatusPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Get the status of a workflow plan.
 *
 * Plan status is determined by examining S3 artifacts:
 * - Uploaded plans: Have plan.json + status.json with "UPLOADED" → Returns UPLOADED status
 * - Completed plans: Have plan.json + status.json with "COMPLETED" → Returns COMPLETED status
 * - In-progress plans: Have maestro_config.json + status.json → Returns current status
 * - Non-existent plans: Have neither plan result nor status → 404 error
 *
 * Status progression with multi-stage planning:
 * - status=PENDING, result_available=False → Planning started, no result yet
 * - status=PENDING, result_available=True, stage_detail="Feasibility completed" → Intermediate result available
 * - status=PENDING, result_available=True, stage_detail="Optimization stage 2 (deadtime) completed" → Stage 2 result available
 * - status=FAILED, result_available=True, stage_detail="..." → Failed but partial result available
 * - status=COMPLETED, result_available=True → Final result available
 *
 * The result_available field explicitly indicates when GET /result will succeed.
 * The stage_detail field provides detailed progress information from Maestro's multi-stage planner.
 */
export const fetchGetPlanStatus = (
  variables: GetPlanStatusVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowPlanStatus,
    GetPlanStatusError,
    undefined,
    {},
    {},
    GetPlanStatusPathParams
  >({
    url: '/v3/workflow/{workflowId}/plan/{planId}/status',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Get the status of a workflow plan.
 *
 * Plan status is determined by examining S3 artifacts:
 * - Uploaded plans: Have plan.json + status.json with "UPLOADED" → Returns UPLOADED status
 * - Completed plans: Have plan.json + status.json with "COMPLETED" → Returns COMPLETED status
 * - In-progress plans: Have maestro_config.json + status.json → Returns current status
 * - Non-existent plans: Have neither plan result nor status → 404 error
 *
 * Status progression with multi-stage planning:
 * - status=PENDING, result_available=False → Planning started, no result yet
 * - status=PENDING, result_available=True, stage_detail="Feasibility completed" → Intermediate result available
 * - status=PENDING, result_available=True, stage_detail="Optimization stage 2 (deadtime) completed" → Stage 2 result available
 * - status=FAILED, result_available=True, stage_detail="..." → Failed but partial result available
 * - status=COMPLETED, result_available=True → Final result available
 *
 * The result_available field explicitly indicates when GET /result will succeed.
 * The stage_detail field provides detailed progress information from Maestro's multi-stage planner.
 */
export const useGetPlanStatus = <TData = Schemas.WorkflowPlanStatus>(
  variables: GetPlanStatusVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.WorkflowPlanStatus,
      GetPlanStatusError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    Schemas.WorkflowPlanStatus,
    GetPlanStatusError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/workflow/{workflowId}/plan/{planId}/status',
      operationId: 'getPlanStatus',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetPlanStatus({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type GetPlanResultPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
  /**
   * @format uuid
   */
  planId: string;
};

export type GetPlanResultError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetPlanResultVariables = {
  pathParams: GetPlanResultPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Get the result of a workflow plan.
 *
 * This endpoint returns the plan result when available. With multi-stage planning, intermediate
 * results are written by Maestro as each stage completes, even while status=PENDING.
 *
 * Check the status endpoint's result_available field to determine if a result is available:
 * - result_available=False → No result yet (planning in progress, no stages completed)
 * - result_available=True → Intermediate or final result available (can call GET /result)
 *
 * The stage_detail field provides human-readable context about which stage completed.
 *
 * Returns 404 if no plan result has been written yet (result_available=False).
 */
export const fetchGetPlanResult = (
  variables: GetPlanResultVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowPlanResult,
    GetPlanResultError,
    undefined,
    {},
    {},
    GetPlanResultPathParams
  >({
    url: '/v3/workflow/{workflowId}/plan/{planId}/result',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Get the result of a workflow plan.
 *
 * This endpoint returns the plan result when available. With multi-stage planning, intermediate
 * results are written by Maestro as each stage completes, even while status=PENDING.
 *
 * Check the status endpoint's result_available field to determine if a result is available:
 * - result_available=False → No result yet (planning in progress, no stages completed)
 * - result_available=True → Intermediate or final result available (can call GET /result)
 *
 * The stage_detail field provides human-readable context about which stage completed.
 *
 * Returns 404 if no plan result has been written yet (result_available=False).
 */
export const useGetPlanResult = <TData = Schemas.WorkflowPlanResult>(
  variables: GetPlanResultVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.WorkflowPlanResult,
      GetPlanResultError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    Schemas.WorkflowPlanResult,
    GetPlanResultError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/workflow/{workflowId}/plan/{planId}/result',
      operationId: 'getPlanResult',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetPlanResult({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type ListPlansPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
};

export type ListPlansError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type ListPlansResponse = Schemas.WorkflowPlanStatus[];

export type ListPlansVariables = {
  pathParams: ListPlansPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * List existing plans for a workflow.
 */
export const fetchListPlans = (
  variables: ListPlansVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    ListPlansResponse,
    ListPlansError,
    undefined,
    {},
    {},
    ListPlansPathParams
  >({
    url: '/v3/workflow/{workflowId}/plan',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * List existing plans for a workflow.
 */
export const useListPlans = <TData = ListPlansResponse>(
  variables: ListPlansVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<ListPlansResponse, ListPlansError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<ListPlansResponse, ListPlansError, TData>({
    queryKey: queryKeyFn({
      path: '/v3/workflow/{workflowId}/plan',
      operationId: 'listPlans',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchListPlans({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type KillPlanPathParams = {
  /**
   * @format uuid
   */
  workflowId: string;
  /**
   * @format uuid
   */
  planId: string;
};

export type KillPlanError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type KillPlanVariables = {
  pathParams: KillPlanPathParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Kill/terminate a running workflow plan.
 */
export const fetchKillPlan = (
  variables: KillPlanVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.WorkflowPlanStatus,
    KillPlanError,
    undefined,
    {},
    {},
    KillPlanPathParams
  >({
    url: '/v3/workflow/{workflowId}/plan/{planId}',
    method: 'delete',
    ...variables,
    signal,
  });

/**
 * Kill/terminate a running workflow plan.
 */
export const useKillPlan = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.WorkflowPlanStatus,
      KillPlanError,
      KillPlanVariables
    >,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useWfbApiContext();
  return reactQuery.useMutation<
    Schemas.WorkflowPlanStatus,
    KillPlanError,
    KillPlanVariables
  >({
    mutationFn: (variables: KillPlanVariables) =>
      fetchKillPlan({ ...fetcherOptions, ...variables }),
    ...options,
  });
};

export type GetScriptsInfoQueryParams = {
  version?: string | null;
};

export type GetScriptsInfoError = Fetcher.ErrorWrapper<{
  status: 422;
  payload: Schemas.HTTPValidationError;
}>;

export type GetScriptsInfoVariables = {
  queryParams?: GetScriptsInfoQueryParams;
} & WfbApiContext['fetcherOptions'];

/**
 * Get information for the specified version of custom scripts for current organization.
 */
export const fetchGetScriptsInfo = (
  variables: GetScriptsInfoVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    Schemas.OrgScriptsInfo,
    GetScriptsInfoError,
    undefined,
    {},
    GetScriptsInfoQueryParams,
    {}
  >({ url: '/v3/scripts/info', method: 'get', ...variables, signal });

/**
 * Get information for the specified version of custom scripts for current organization.
 */
export const useGetScriptsInfo = <TData = Schemas.OrgScriptsInfo>(
  variables: GetScriptsInfoVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.OrgScriptsInfo,
      GetScriptsInfoError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    Schemas.OrgScriptsInfo,
    GetScriptsInfoError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/scripts/info',
      operationId: 'getScriptsInfo',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetScriptsInfo({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type ListScriptsVersionsError = Fetcher.ErrorWrapper<undefined>;

export type ListScriptsVersionsResponse = string[];

export type ListScriptsVersionsVariables = WfbApiContext['fetcherOptions'];

/**
 * Get list of all available of scripts package versions for current organization.
 */
export const fetchListScriptsVersions = (
  variables: ListScriptsVersionsVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<
    ListScriptsVersionsResponse,
    ListScriptsVersionsError,
    undefined,
    {},
    {},
    {}
  >({ url: '/v3/scripts', method: 'get', ...variables, signal });

/**
 * Get list of all available of scripts package versions for current organization.
 */
export const useListScriptsVersions = <TData = ListScriptsVersionsResponse>(
  variables: ListScriptsVersionsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      ListScriptsVersionsResponse,
      ListScriptsVersionsError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<
    ListScriptsVersionsResponse,
    ListScriptsVersionsError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/v3/scripts',
      operationId: 'listScriptsVersions',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchListScriptsVersions({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type GetApiVersionError = Fetcher.ErrorWrapper<undefined>;

export type GetApiVersionResponse = {
  [key: string]: string;
};

export type GetApiVersionVariables = WfbApiContext['fetcherOptions'];

/**
 * Return the API version.
 * :return: the dict with the API version.
 */
export const fetchGetApiVersion = (
  variables: GetApiVersionVariables,
  signal?: AbortSignal
) =>
  wfbApiFetch<GetApiVersionResponse, GetApiVersionError, undefined, {}, {}, {}>(
    { url: '/version', method: 'get', ...variables, signal }
  );

/**
 * Return the API version.
 * :return: the dict with the API version.
 */
export const useGetApiVersion = <TData = GetApiVersionResponse>(
  variables: GetApiVersionVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetApiVersionResponse,
      GetApiVersionError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useWfbApiContext(options);
  return reactQuery.useQuery<GetApiVersionResponse, GetApiVersionError, TData>({
    queryKey: queryKeyFn({
      path: '/version',
      operationId: 'getApiVersion',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetApiVersion({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  });
};

export type QueryOperation =
  | {
      path: '/v3/driver/{scheduler}/{schedulerVersion}';
      operationId: 'getAllDrivers';
      variables: GetAllDriversVariables;
    }
  | {
      path: '/v3/driver/{scheduler}/{schedulerVersion}/{driverName}';
      operationId: 'getDriverVersions';
      variables: GetDriverVersionsVariables;
    }
  | {
      path: '/v3/driver/{scheduler}/{schedulerVersion}/{driverName}/{driverVersion}';
      operationId: 'getDriver';
      variables: GetDriverVariables;
    }
  | {
      path: '/v3/workcell/transport';
      operationId: 'getTransportConfig';
      variables: GetTransportConfigVariables;
    }
  | {
      path: '/v3/workcell/transport/versions';
      operationId: 'getTransportConfigVersions';
      variables: GetTransportConfigVersionsVariables;
    }
  | {
      path: '/v3/workcell/transport/list';
      operationId: 'getAllTransportConfigs';
      variables: GetAllTransportConfigsVariables;
    }
  | {
      path: '/v3/workcell/hub/{commandId}/status';
      operationId: 'getCommandStatus';
      variables: GetCommandStatusVariables;
    }
  | {
      path: '/v3/workflow/scheduler_versions';
      operationId: 'getSchedulerVersions';
      variables: GetSchedulerVersionsVariables;
    }
  | {
      path: '/v3/workflow/{id}';
      operationId: 'getWorkflow';
      variables: GetWorkflowVariables;
    }
  | {
      path: '/v3/workflow';
      operationId: 'listWorkflows';
      variables: ListWorkflowsVariables;
    }
  | {
      path: '/v3/workflow/info/{id}';
      operationId: 'getWorkflowInfo';
      variables: GetWorkflowInfoVariables;
    }
  | {
      path: '/v3/workflow/{workflowId}/plan/{planId}/status';
      operationId: 'getPlanStatus';
      variables: GetPlanStatusVariables;
    }
  | {
      path: '/v3/workflow/{workflowId}/plan/{planId}/result';
      operationId: 'getPlanResult';
      variables: GetPlanResultVariables;
    }
  | {
      path: '/v3/workflow/{workflowId}/plan';
      operationId: 'listPlans';
      variables: ListPlansVariables;
    }
  | {
      path: '/v3/scripts/info';
      operationId: 'getScriptsInfo';
      variables: GetScriptsInfoVariables;
    }
  | {
      path: '/v3/scripts';
      operationId: 'listScriptsVersions';
      variables: ListScriptsVersionsVariables;
    }
  | {
      path: '/version';
      operationId: 'getApiVersion';
      variables: GetApiVersionVariables;
    };
